import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, Phone, Mail, Euro, BookOpen, Utensils, AlertTriangle, Pill, Salad, ShieldCheck, ShieldAlert, Award, Users, Send, Loader2, X, Receipt } from "lucide-react";

const fmtEuro = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €");

// Riquadri della scheda selezionabili all'invio del report (ordine = ordine di stampa)
const SECTION_DEFS = [
  { key: "anagrafica", label: "Dati anagrafici" },
  { key: "contatti", label: "Contatti anagrafica" },
  { key: "isee", label: "ISEE" },
  { key: "agevolazione", label: "Agevolazione" },
  { key: "contabile", label: "Situazione contabile" },
  { key: "movimenti", label: "Lista movimenti" },
  { key: "extra", label: "Iscrizioni attività Extra" },
  { key: "mensa_matrice", label: "Giorni a mensa (matrice)" },
  { key: "spot", label: "Iscrizioni Mensa Spot" },
  { key: "diete", label: "Intolleranze / Diete" },
  { key: "farmaci", label: "Farmaci" },
  { key: "moduli", label: "Idoneità sportiva / Moduli" },
  { key: "deleghe", label: "Deleghe al ritiro" },
];

const STATO_AGEV_LBL = {
  BOZZA: "Bozza",
  APPROVATA: "Approvata",
  FIRMATA: "Firmata",
  RATE: "Rate",
  NO: "Negata",
  USCITO: "Uscito",
  APPUNTAMENTO: "Appuntamento",
  SC_FRATELLI: "Sconto fratelli",
};
const annoLbl = (a) => (a ? `${a}/${String(a + 1).slice(-2)}` : "—");

const Section = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId} style={{ background: "white", border: "1px solid #e3e8ef", borderRadius: 10, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: "#334155", fontWeight: 800, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>
      <Icon size={16} strokeWidth={2.4} /> {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "3px 0", fontSize: 13, borderBottom: "1px dashed #eef2f7" }}>
    <span style={{ color: "#64748b" }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value || "—"}</span>
  </div>
);

function SendLinkModal({ alunnoCod, anno, nome, onClose }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [sel, setSel] = useState(() => new Set(SECTION_DEFS.map((s) => s.key)));
  const annoLbl2 = anno ? `${anno}/${String(anno + 1).slice(-2)}` : "—";

  const toggle = (key) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allSel = sel.size === SECTION_DEFS.length;
  const toggleAll = () => setSel(allSel ? new Set() : new Set(SECTION_DEFS.map((s) => s.key)));

  const submit = async () => {
    const em = email.trim();
    if (!em || !em.includes("@")) { toast.error("Inserisci un indirizzo email valido"); return; }
    const sezioni = SECTION_DEFS.map((s) => s.key).filter((k) => sel.has(k));
    if (sezioni.length === 0) { toast.error("Seleziona almeno un riquadro da includere"); return; }
    setSending(true);
    try {
      const r = await api.post("/alunni-portal/scheda/invia-link", { alunno_cod: alunnoCod, anno, email: em, sezioni });
      setSent(r.data);
      toast.success(`Link inviato a ${em}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="scheda-send-link-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 460, borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.3)", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#1e3a8a", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ fontSize: 14 }}><Send size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />Invia scheda via email</strong>
          <button onClick={onClose} data-testid="scheda-send-link-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ padding: 18, overflowY: "auto" }}>
          {sent ? (
            <div data-testid="scheda-send-link-success" style={{ fontSize: 14, color: "#0f172a" }}>
              <div style={{ color: "#16a34a", fontWeight: 700, marginBottom: 8 }}>✓ Link inviato correttamente a <b>{sent.email}</b></div>
              <div style={{ fontSize: 12, color: "#64748b" }}>Il link è valido 7 giorni e apre la scheda in sola lettura senza login.</div>
              <button onClick={onClose} data-testid="scheda-send-link-done"
                style={{ marginTop: 16, background: "#1e3a8a", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Chiudi</button>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
                Invia un link per consultare la scheda di <b>{nome}</b> (anno <b>{annoLbl2}</b>).
                Il destinatario aprirà la scheda in sola lettura, senza utente né password. Il link scade dopo 7 giorni.
              </div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Email destinatario</label>
              <input data-testid="scheda-send-link-email" type="email" value={email} autoFocus
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="nome@esempio.it"
                style={{ width: "100%", marginTop: 6, padding: "9px 11px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />

              <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Riquadri da includere nel report</label>
                <button type="button" onClick={toggleAll} data-testid="scheda-send-sezioni-toggle-all"
                  style={{ background: "none", border: "none", color: "#1e3a8a", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  {allSel ? "Deseleziona tutti" : "Seleziona tutti"}
                </button>
              </div>
              <div data-testid="scheda-send-sezioni-list" style={{ marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 8, maxHeight: 240, overflowY: "auto" }}>
                {SECTION_DEFS.map((s) => (
                  <label key={s.key} data-testid={`scheda-send-sezione-${s.key}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
                    <input type="checkbox" checked={sel.has(s.key)} onChange={() => toggle(s.key)}
                      data-testid={`scheda-send-sezione-cb-${s.key}`} />
                    {s.label}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{sel.size}/{SECTION_DEFS.length} riquadri selezionati</div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button onClick={onClose} data-testid="scheda-send-link-cancel"
                  style={{ background: "#f1f5f9", color: "#334155", border: "none", padding: "8px 14px", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Annulla</button>
                <button onClick={submit} disabled={sending} data-testid="scheda-send-link-submit"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#1e3a8a", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: sending ? "wait" : "pointer", opacity: sending ? 0.7 : 1 }}>
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {sending ? "Invio…" : "Invia link"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SchedaRiassuntiva({ alunnoCod = null, anno = null, publicToken = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const allowSendLink = !!alunnoCod && !publicToken;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      let r;
      if (publicToken) {
        r = await api.get(`/alunni-portal/scheda-pubblica/${publicToken}`);
      } else {
        const params = {};
        if (anno) params.anno = anno;
        if (alunnoCod) params.alunno_cod = alunnoCod;
        r = await api.get("/alunni-portal/scheda", { params });
      }
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento scheda");
      setData(null);
    } finally { setLoading(false); }
  }, [alunnoCod, anno, publicToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div data-testid="scheda-loading" style={{ padding: 20, color: "#64748b" }}>Caricamento scheda…</div>;
  if (err) return <div data-testid="scheda-error" style={{ padding: 16, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>{err}</div>;
  if (!data) return null;

  const a = data.anagrafica || {};
  const sc = data.situazione_contabile || {};
  const irregolare = sc.stato === "IRREGOLARE";
  // In vista pubblica (link inviato) mostra solo i riquadri selezionati all'invio; altrove tutti.
  const allowed = Array.isArray(data.sezioni) ? new Set(data.sezioni) : null;
  const show = (k) => !allowed || allowed.has(k);

  return (
    <div data-testid="scheda-riassuntiva" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Intestazione + situazione contabile */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12,
        background: "linear-gradient(135deg,#1e3a8a,#3b82f6)", color: "white", borderRadius: 12, padding: "16px 20px" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800 }} data-testid="scheda-nome">{a.cognome} {a.nome}</div>
          <div style={{ opacity: 0.85, fontSize: 13 }} data-testid="scheda-classe-header">
            Anno {annoLbl(data.anno)} · cod. {a.cod}
            {(a.settore || a.classe) ? ` · ${[a.settore, a.classe].filter(Boolean).join(" ")}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {allowSendLink && (
            <button data-testid="scheda-invia-link-btn" onClick={() => setShowSend(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.4)", color: "white", padding: "7px 12px", borderRadius: 8,
                fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              <Send size={14} /> Invia scheda via email
            </button>
          )}
          <div data-testid="scheda-stato-contabile" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: irregolare ? "#dc2626" : "#16a34a",
            padding: "8px 14px", borderRadius: 999, fontWeight: 800, fontSize: 13 }}>
            {irregolare ? <ShieldAlert size={18} /> : <ShieldCheck size={18} />} {sc.stato}
          </div>
        </div>
      </div>

      {showSend && (
        <SendLinkModal alunnoCod={alunnoCod} anno={data.anno} nome={`${a.cognome} ${a.nome}`.trim()} onClose={() => setShowSend(false)} />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
        {show("anagrafica") && (
        <Section icon={User} title="Dati anagrafici" testId="scheda-anagrafica">
          <Row label="Codice fiscale" value={a.codice_fiscale} />
          <Row label="Settore / Classe" value={[a.settore, a.classe].filter(Boolean).join(" ")} />
          <Row label="Nascita" value={a.nascita} />
          <Row label="Luogo" value={a.luogo_nascita} />
          <Row label="Indirizzo" value={[a.indirizzo, a.cap, a.comune].filter(Boolean).join(" · ")} />
        </Section>
        )}

        {show("contatti") && (
        <Section icon={Phone} title="Contatti anagrafica" testId="scheda-contatti">
          {(data.telefoni || []).length ? (data.telefoni).map((t, i) => (
            <Row key={i} label={<span><Phone size={12} /> Telefono</span>} value={t} />
          )) : <Row label="Telefono" value="—" />}
          {(data.emails || []).length ? (data.emails).map((e, i) => (
            <Row key={i}
              label={<span><Mail size={12} /> Email{e.is_preferred ? " ★" : ""}</span>}
              value={e.email} />
          )) : <Row label={<span><Mail size={12} /> Email</span>} value={data.email} />}
        </Section>
        )}

        {show("isee") && (
        <Section icon={Euro} title="ISEE" testId="scheda-isee">
          <div style={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>
            {data.isee?.valore != null ? fmtEuro(data.isee.valore) : "Non disponibile"}
          </div>
          {data.isee?.anno ? <div style={{ fontSize: 12, color: "#64748b" }}>Anno {annoLbl(data.isee.anno)}</div> : null}
        </Section>
        )}

        {show("agevolazione") && data.agevolazione?.agevolato ? (
          <Section icon={Award} title="Agevolazione" testId="scheda-agevolazione">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ background: "#1b5e20", color: "white", fontWeight: 800, fontSize: 12, padding: "3px 10px", borderRadius: 999 }}>
                ✓ AGEVOLATO · {annoLbl(data.anno)}
              </span>
              {data.agevolazione?.stato ? (
                <span style={{ fontSize: 13, color: "#334155" }}>
                  Stato: <b>{STATO_AGEV_LBL[data.agevolazione.stato] || data.agevolazione.stato}</b>
                </span>
              ) : null}
            </div>
          </Section>
        ) : null}

        {show("contabile") && (
        <Section icon={ShieldCheck} title="Situazione contabile" testId="scheda-contabile-dett">
          <div style={{ fontWeight: 800, color: irregolare ? "#dc2626" : "#16a34a", marginBottom: 6 }}>{sc.stato}</div>
          {irregolare ? (sc.movimenti_scaduti_non_pagati || []).map((m, i) => (
            <div key={i} style={{ fontSize: 12, borderBottom: "1px dashed #eef2f7", padding: "3px 0", display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ color: "#334155" }}>{m.descrizione} <span style={{ color: "#94a3b8" }}>(scad. {m.scadenza})</span></span>
              <b style={{ color: "#dc2626" }}>{fmtEuro(m.importo)}</b>
            </div>
          )) : <div style={{ fontSize: 13, color: "#16a34a" }}>Nessun movimento scaduto non pagato.</div>}
        </Section>
        )}

        {show("extra") && (
        <Section icon={BookOpen} title="Iscrizioni attività Extra" testId="scheda-extra">
          {(data.iscrizioni_extra || []).length ? (data.iscrizioni_extra).map((x, i) => (
            <div key={i} style={{ fontSize: 13, borderBottom: "1px dashed #eef2f7", padding: "4px 0" }}>
              <b>{x.corso}</b> <span style={{ color: "#94a3b8" }}>· {annoLbl(x.anno)}</span>
              <div style={{ fontSize: 12, color: "#64748b" }}>{(x.giorni || []).join(", ") || "—"} · {fmtEuro(x.prezzo)} · {x.stato}</div>
            </div>
          )) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessuna iscrizione.</div>}
        </Section>
        )}

        {show("mensa_matrice") && (
        <Section icon={Utensils} title="Giorni a mensa (matrice)" testId="scheda-mensa-matrice">
          {(data.mensa_matrice || []).length ? (data.mensa_matrice).map((g, i) => (
            <div key={i} style={{ fontSize: 13, borderBottom: "1px dashed #eef2f7", padding: "4px 0", display: "flex", gap: 10 }}>
              <b style={{ minWidth: 34, color: "#1e3a8a" }}>{g.giorno}</b>
              <span style={{ color: "#334155" }}>{(g.motivi || []).join(" · ")}</span>
            </div>
          )) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessun giorno a mensa nella matrice.</div>}
        </Section>
        )}

        {show("spot") && (
        <Section icon={Utensils} title="Iscrizioni Mensa Spot" testId="scheda-spot">
          {(data.mensa_spot || []).length ? (data.mensa_spot).map((x, i) => (
            <div key={i} style={{ fontSize: 13, borderBottom: "1px dashed #eef2f7", padding: "4px 0" }}>
              <b>{x.giorni || "—"}</b> <span style={{ color: "#94a3b8" }}>· {annoLbl(x.anno)} · {x.stato}</span>
            </div>
          )) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessuna richiesta spot.</div>}
        </Section>
        )}

        {show("diete") && (
        <Section icon={Salad} title="Intolleranze / Diete" testId="scheda-diete">
          <div style={{ fontSize: 13, fontWeight: data.intolleranze_diete ? 700 : 400, textTransform: "uppercase",
            color: data.intolleranze_diete ? "#ad1457" : "#94a3b8" }}>
            {data.intolleranze_diete || "Nessuna segnalazione."}
          </div>
        </Section>
        )}

        {show("farmaci") && (
        <Section icon={Pill} title="Farmaci" testId="scheda-farmaci">
          {(data.farmaci || []).length ? (data.farmaci).map((f, i) => (
            <div key={i} style={{ borderBottom: "1px dashed #eef2f7", padding: "5px 0" }}>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>Anno {annoLbl(f.anno)}</div>
              {(f.voci || []).map((v, j) => (
                <div key={j} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, padding: "1px 0" }}>
                  <span style={{ color: "#64748b" }}>{v.campo}</span>
                  <span style={{ fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{v.valore}</span>
                </div>
              ))}
            </div>
          )) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessun farmaco registrato.</div>}
        </Section>
        )}

        {show("moduli") && (
        <Section icon={Award} title="Idoneità sportiva / Moduli" testId="scheda-moduli">
          {(data.moduli?.voci || []).length ? (
            <>
              {data.moduli?.anno ? <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, marginBottom: 4 }}>Anno {annoLbl(data.moduli.anno)}</div> : null}
              {(data.moduli.voci).map((m, i) => (
                <div key={i} style={{ borderBottom: "1px dashed #eef2f7", padding: "4px 0", fontSize: 13 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <b style={{ color: "#0f172a" }}>{m.categoria}</b>
                    <span style={{ color: m.ha_file ? "#16a34a" : "#94a3b8", fontWeight: 700 }}>{m.ha_file ? "✓ documento" : "— nessun file"}</span>
                  </div>
                  {(m.protocollo || m.valido_fino_al) && (
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      {m.protocollo ? `Prot. ${m.protocollo}` : ""}
                      {m.protocollo_at ? ` del ${m.protocollo_at}` : ""}
                      {m.valido_fino_al ? ` · valido fino al ${m.valido_fino_al}` : ""}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessun modulo / documento registrato.</div>}
        </Section>
        )}

        {show("deleghe") && (
        <Section icon={Users} title="Deleghe al ritiro" testId="scheda-deleghe">
          {(data.deleghe || []).length ? (data.deleghe).map((d, i) => (
            <div key={i} style={{ borderBottom: "1px dashed #eef2f7", padding: "5px 0" }}>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>Anno {annoLbl(d.anno)}</div>
              {(d.voci || []).map((v, j) => (
                <div key={j} style={{ fontSize: 13, padding: "1px 0" }}>
                  <b style={{ color: "#0f172a" }}>{v.nome}</b>
                  {(v.documento || v.scadenza) && (
                    <span style={{ fontSize: 11, color: "#64748b" }}>
                      {v.documento ? ` · ${v.documento}` : ""}{v.scadenza ? ` · scad. ${v.scadenza}` : ""}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessuna delega registrata.</div>}
        </Section>
        )}
      </div>

      {show("movimenti") && (
      <Section icon={Receipt} title="Lista movimenti" testId="scheda-movimenti">
        {(data.movimenti || []).length ? (data.movimenti).map((m, i) => (
          <div key={i} style={{ fontSize: 13, borderBottom: "1px dashed #eef2f7", padding: "5px 0", display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span style={{ color: "#334155" }}>
              {m.descrizione || "—"}
              <span style={{ color: "#94a3b8" }}> · scad. {m.scadenza || "—"}{m.pagato ? ` · pag. ${m.pagamento || "sì"}` : ""}</span>
            </span>
            <b style={{ color: m.pagato ? "#16a34a" : (m.scaduto_non_pagato ? "#dc2626" : "#334155"), whiteSpace: "nowrap" }}>{fmtEuro(m.importo)}</b>
          </div>
        )) : <div style={{ fontSize: 13, color: "#94a3b8" }}>Nessun movimento.</div>}
      </Section>
      )}

      <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "right" }}>
        <AlertTriangle size={11} /> Anno scolastico {annoLbl(data.anno)} · i movimenti includono anche l'anno precedente · scheda in sola lettura
      </div>
    </div>
  );
}
